// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { Contract } from 'model/Contract';
import React from 'react';
import { useAuthContext } from '@tls/auth';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Dialog, FooterButton } from '@tls/treact-ui';
import { contactLines } from './contactLines';

const orDash = (value?: string | null) => (value?.trim() ? value : '-'); // show "-" when no info available

export default function ContractDetailsDialog({ contract, onClose }: Readonly<Props>) {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const isSuperAdmin = currentUser?.roles['SuperAdmin'] ?? false; // license source is admin-only

  return (
    <Dialog
      enableClosing
      open={!!contract}
      size='auto'
      onClose={onClose}
      data-testid='settings-contract-details-dialog'
    >
      <Dialog.DialogHeader label={t('settings.contracts.detailsTitle')} showCloseButton />
      <Dialog.DialogContent>
        <DetailsGrid data-testid='settings-contract-details-content'>
          <span>{t('settings.contracts.contractId')}</span>
          <span>{orDash(contract?.contractId)}</span>
          {isSuperAdmin ? (
            <>
              <span>{t('settings.contracts.materialNumber')}</span>
              <span>{orDash(contract?.materialNumber)}</span>
              <span>{t('settings.contracts.customerNumber')}</span>
              <span>{orDash(contract?.customerNumber)}</span>
              <span>{t('settings.contracts.endCustomerNumber')}</span>
              <span>{orDash(contract?.endCustomerNumber)}</span>
              <span>{t('settings.contracts.zeCustomerNumber')}</span>
              <span>{orDash(contract?.zeCustomerNumber)}</span>
              <span>{t('settings.contracts.licenseSource')}</span>
              <span>{orDash(contract?.type)}</span>
            </>
          ) : (
            <>
              <span>{t('settings.contracts.customerNumber')}</span>
              <span>{contract?.zeCustomerNumber || contract?.endCustomerNumber || contract?.customerNumber}</span>
              <span>{t('settings.contracts.contact')}</span>
              <PreLine>{contract ? contactLines(contract).join('\n') : ''}</PreLine>
            </>
          )}
        </DetailsGrid>
      </Dialog.DialogContent>
      <Dialog.Footer
        right={[
          <FooterButton
            key='close'
            label={t('close')}
            onClick={onClose}
            variant='secondary'
            data-testid='settings-contract-details-close-button'
          />,
        ]}
      />
    </Dialog>
  );
}

interface Props {
  contract: Contract | undefined;
  onClose: () => void;
}

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.5rem 1.5rem;
  overflow-wrap: anywhere;
`;

const PreLine = styled.span`
  white-space: pre-line;
`;
